/**
 * Shared site-field definitions used by both SiteDetail (edit) and
 * SiteCreate (create). Single source of truth so the two surfaces
 * never drift on enum values or labels.
 *
 * Enum values are lowercase to match the backend (server returns
 * lowercase; uppercase round-trips for save but breaks the dropdown).
 */

export type FieldType = 'text' | 'number' | 'date' | 'boolean' | 'select' | 'textarea';

export interface FieldOption {
  value: string;
  label: string;
}

export interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  options?: FieldOption[];
  unit?: string;
  readOnly?: boolean;
  step?: string;
  /**
   * Display scale for numeric fields. Stored value is multiplied by this
   * on display, and the user's input is divided by it on save. Used to let
   * a column stored as μg/L surface as mg/L (scale=0.001) per the ARGOS
   * policy that all concentrations be expressed in mg/L.
   */
  displayScale?: number;
  /**
   * Single-line caption rendered under the field. Use for non-obvious
   * defaults, precedence rules, or units conventions — kept short
   * (one line) so the form stays scannable.
   */
  helpText?: string;
}

export const AQUIFER_OPTIONS: FieldOption[] = [
  { value: '', label: '--' },
  { value: 'ogallala', label: 'Ogallala' },
  { value: 'dockum', label: 'Dockum' },
  { value: 'edwards', label: 'Edwards' },
  { value: 'trinity', label: 'Trinity' },
  { value: 'gulf_coast', label: 'Gulf Coast' },
  { value: 'alluvial', label: 'Alluvial' },
  { value: 'fractured_bedrock', label: 'Fractured Bedrock' },
  { value: 'perched', label: 'Perched' },
  { value: 'composite', label: 'Composite' },
  { value: 'other', label: 'Other' },
];

export const CONTAMINANT_OPTIONS: FieldOption[] = [
  { value: '', label: '--' },
  { value: 'btex', label: 'BTEX' },
  { value: 'tph', label: 'TPH' },
  { value: 'PSH', label: 'PSH / LNAPL' },
  { value: 'chlorinated_solvents', label: 'Chlorinated Solvents' },
  { value: 'pfas', label: 'PFAS' },
  { value: 'metals', label: 'Metals' },
  { value: 'pesticides', label: 'Pesticides' },
  { value: 'radionuclides', label: 'Radionuclides' },
  { value: 'mixed', label: 'Mixed' },
  { value: 'other', label: 'Other (specify below)' },
];

export const STATUS_OPTIONS: FieldOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'closure_pending', label: 'Closure Pending' },
  { value: 'closed', label: 'Closed' },
  { value: 'inactive', label: 'Inactive' },
];

// Mirrors src/models/site.py RegulatoryProgram enum.
export const REGULATORY_PROGRAM_OPTIONS: FieldOption[] = [
  { value: '', label: '--' },
  { value: 'tceq_pst', label: 'TCEQ PST' },
  { value: 'tceq_ihw', label: 'TCEQ IHW' },
  { value: 'rrc_oilgas', label: 'RRC Oil & Gas' },
  { value: 'epa_rcra', label: 'EPA RCRA' },
  { value: 'epa_cercla', label: 'EPA CERCLA / Superfund' },
  { value: 'state_vcp', label: 'State VCP' },
  { value: 'other', label: 'Other' },
];

// US states + territories. The DB column is `String(2)` so we have to send
// the 2-letter postal code; surfacing the full name in the dropdown means
// users don't have to remember the abbreviation (beta tester typed "Texas"
// and got silent rejection).
export const US_STATE_OPTIONS: FieldOption[] = [
  { value: '', label: '--' },
  { value: 'AL', label: 'Alabama (AL)' },
  { value: 'AK', label: 'Alaska (AK)' },
  { value: 'AZ', label: 'Arizona (AZ)' },
  { value: 'AR', label: 'Arkansas (AR)' },
  { value: 'CA', label: 'California (CA)' },
  { value: 'CO', label: 'Colorado (CO)' },
  { value: 'CT', label: 'Connecticut (CT)' },
  { value: 'DE', label: 'Delaware (DE)' },
  { value: 'DC', label: 'District of Columbia (DC)' },
  { value: 'FL', label: 'Florida (FL)' },
  { value: 'GA', label: 'Georgia (GA)' },
  { value: 'HI', label: 'Hawaii (HI)' },
  { value: 'ID', label: 'Idaho (ID)' },
  { value: 'IL', label: 'Illinois (IL)' },
  { value: 'IN', label: 'Indiana (IN)' },
  { value: 'IA', label: 'Iowa (IA)' },
  { value: 'KS', label: 'Kansas (KS)' },
  { value: 'KY', label: 'Kentucky (KY)' },
  { value: 'LA', label: 'Louisiana (LA)' },
  { value: 'ME', label: 'Maine (ME)' },
  { value: 'MD', label: 'Maryland (MD)' },
  { value: 'MA', label: 'Massachusetts (MA)' },
  { value: 'MI', label: 'Michigan (MI)' },
  { value: 'MN', label: 'Minnesota (MN)' },
  { value: 'MS', label: 'Mississippi (MS)' },
  { value: 'MO', label: 'Missouri (MO)' },
  { value: 'MT', label: 'Montana (MT)' },
  { value: 'NE', label: 'Nebraska (NE)' },
  { value: 'NV', label: 'Nevada (NV)' },
  { value: 'NH', label: 'New Hampshire (NH)' },
  { value: 'NJ', label: 'New Jersey (NJ)' },
  { value: 'NM', label: 'New Mexico (NM)' },
  { value: 'NY', label: 'New York (NY)' },
  { value: 'NC', label: 'North Carolina (NC)' },
  { value: 'ND', label: 'North Dakota (ND)' },
  { value: 'OH', label: 'Ohio (OH)' },
  { value: 'OK', label: 'Oklahoma (OK)' },
  { value: 'OR', label: 'Oregon (OR)' },
  { value: 'PA', label: 'Pennsylvania (PA)' },
  { value: 'PR', label: 'Puerto Rico (PR)' },
  { value: 'RI', label: 'Rhode Island (RI)' },
  { value: 'SC', label: 'South Carolina (SC)' },
  { value: 'SD', label: 'South Dakota (SD)' },
  { value: 'TN', label: 'Tennessee (TN)' },
  { value: 'TX', label: 'Texas (TX)' },
  { value: 'UT', label: 'Utah (UT)' },
  { value: 'VT', label: 'Vermont (VT)' },
  { value: 'VA', label: 'Virginia (VA)' },
  { value: 'WA', label: 'Washington (WA)' },
  { value: 'WV', label: 'West Virginia (WV)' },
  { value: 'WI', label: 'Wisconsin (WI)' },
  { value: 'WY', label: 'Wyoming (WY)' },
];
